<template>
    <!-- StatsCard.vue -->
    <v-slide-y-transition hide-on-leave>
        <v-card
            :key="`${item.uuid}_${item.value}`"
            :class="`fill-height ${colorModifier}`"
            :color="color"
            :height="height"
            dark
        >
            <!-- Title -->
            <v-card-subtitle class="font-weight-black text-h2 text-xl-h1 white--text">
                {{ valueFormatted }}
                <!-- Crisis / Good icon -->
                <v-icon
                    v-if="icon"
                    class="float-right"
                    right
                >
                    {{ icon }}
                </v-icon>
            </v-card-subtitle>
            <!-- Subtitle -->
            <v-card-text class="font-weight-bold text-h4 text-xl-h3 white--text">
                {{ item.title }}
            </v-card-text>
        </v-card>
    </v-slide-y-transition>
</template>

<script>
    import {contains, isArray} from 'underscore'
    import dateUtils from '@windsor-telecom/client-core/src/utils/date'

    export default {
        name: 'StatsCard',
        props: {
            /**
             * @type {{
             * uuid: string,
             * title: string,
             * unit: string,
             * crisisValue: number,
             * actionableBadValue: number,
             * actionableGoodValue: number,
             * goodValue: number,
             * orderId: number,
             * disableThresholds: boolean
             * }}
             */
            item: {
                type: Object,
                required: true
            },
            /**
             * The height of the card
             */
            height: {
                type: Number
            }
        },
        computed: {
            badRange () {
                const vm = this
                // Sort values so lowest value is first
                return vm.sortRange([vm.item.crisisValue, vm.item.actionableBadValue])
            },
            color () {
                const vm = this
                const neutralColor = 'info'

                if (vm.isNeutral) {
                    return neutralColor
                }

                if (vm.isActionableGood || vm.isGood) {
                    return 'good'
                }

                if (vm.isActionableBad || vm.isCrisis) {
                    return 'crisis'
                }

                return neutralColor
            },
            colorModifier () {
                const vm = this

                if (vm.isCrisis || vm.isGood || vm.isNeutral) {
                    return ''
                }

                if (contains([vm.item.actionableGoodValue, vm.item.actionableBadValue], vm.value)) {
                    return 'lighten-3'
                }

                return 'lighten-2'
            },
            goodRange () {
                const vm = this
                // Sort values so lowest value is first
                return vm.sortRange([vm.item.goodValue, vm.item.actionableGoodValue])
            },
            icon () {
                const vm = this

                if (!vm.isNeutral && (vm.isCrisis || vm.isGood)) {
                    return vm.isCrisis ? 'mdi-alert' : 'mdi-check-circle'
                }

                return null
            },
            isActionableBad () {
                const vm = this

                return vm.value >= vm.badRange[0] && vm.value <= vm.badRange[1]
            },
            isActionableGood () {
                const vm = this

                return vm.value >= vm.goodRange[0] && vm.value <= vm.goodRange[1]
            },
            isCrisis () {
                const vm = this

                return vm.isGoodValueHigher
                    ? vm.value <= vm.item.crisisValue
                    : vm.value >= vm.item.crisisValue
            },
            isGood () {
                const vm = this

                return vm.isGoodValueHigher
                    ? vm.value >= vm.item.goodValue
                    : vm.value <= vm.item.goodValue
            },
            isGoodValueHigher () {
                const vm = this
                return vm.item.goodValue > vm.item.crisisValue
            },
            isNeutral () {
                const vm = this

                // If threshold is disabled card is always neutral
                if (vm.item.disableThresholds) {
                    return true
                }

                // Sort values so lowest value is first
                const items = [vm.item.actionableBadValue, vm.item.actionableGoodValue].sort()

                return vm.value > items[0] && vm.value < items[1]
            },
            value () {
                return this.item.value || 0
            },
            valueFormatted () {
                const vm = this
                const value = vm.value

                switch (vm.item.unit) {
                    case 'seconds':
                        return dateUtils.formatDuration(value)
                    case 'percent':
                        return `${value}%`
                    default:
                        return value
                }
            }
        },
        methods: {
            sortRange (data) {
                return isArray(data) && data.sort((a, b) => a - b)
            }
        }
    }
</script>
